import {FC, useState, useEffect, useMemo, useRef} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {showSwal} from '../../../../ui-kit'
import {Card, Row, Col, Form, Button, Spinner} from 'react-bootstrap'
import {getAgreementById, submitReorderChapter} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams} from 'react-router-dom'
import {AgreementChapter, ContentItem} from './core/_models'
import './TradeDataAgreementChapterPage.scss'

import ModalMoveChapter from './component/modalMoveChapter'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import {useAbility} from '@casl/react'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'



const AgreementChapterReorderPage: FC<{subject: string}> = ({subject}) => {
  const ability = useAbility(AbilityContext)
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idAgreement} = useParams()
  const {idChapter} = useParams()
  const isEditMode = Boolean(idChapter)

  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)

  const [highlightedContentId, setHighlightedContentId] = useState<string | null>(null)

  const [originalOrder, setOriginalOrder] = useState<string[]>([])
  const [contents, setContents] = useState<ContentItem[]>([])
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
    const updated = [...list]
    const [moved] = updated.splice(from, 1)
    updated.splice(to, 0, moved)
    return updated
  }

  const highlight = (id: string) => {
    setHighlightedContentId(id)

    setTimeout(() => {
      setHighlightedContentId(null)
    }, 1000)
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return

    setContents(moveItem(contents, index, index - 1))
    highlight(contents[index].id)
  }

  const handleMoveDown = (index: number) => {
    if (index === contents.length - 1) return

    setContents(moveItem(contents, index, index + 1))
    highlight(contents[index].id)
  }

  const [moveForm, setMoveForm] = useState({
    sourceChapterId: '',
    action: 'after' as 'before' | 'after' | 'position',
    targetChapterId: '',
    targetPosition: '',
  })

  const handleMoveValueChange = (name: string, value: string) => {
    setMoveForm((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const openMoveModal = (sourceChapterId = '') => {
    setMoveForm((prev) => ({...prev, sourceChapterId}))
    setIsMoveModalOpen(true)
  }

  const handleMoveChapter = () => {
    const {sourceChapterId, targetChapterId, action, targetPosition} = moveForm

    if (!sourceChapterId) return

    const sourceIndex = contents.findIndex((c) => String(c.id) === sourceChapterId)
    if (sourceIndex === -1) return

    let insertIndex: number

    if (action === 'position') {
      const position = Number(targetPosition)
      if (!Number.isInteger(position) || position < 1 || position > contents.length) return

      // A position is where the article ends up once it has been lifted out,
      // so this index needs no correction for the removal below.
      insertIndex = position - 1
    } else {
      if (!targetChapterId || sourceChapterId === targetChapterId) return

      const targetIndex = contents.findIndex((c) => String(c.id) === targetChapterId)
      if (targetIndex === -1) return

      insertIndex = action === 'after' ? targetIndex + 1 : targetIndex

      // The target index was read before the article was lifted out.
      if (sourceIndex < insertIndex) {
        insertIndex -= 1
      }
    }

    const updated = [...contents]
    const [moved] = updated.splice(sourceIndex, 1)
    updated.splice(insertIndex, 0, moved)

    setContents(updated)

    setTimeout(() => {
      const el = rowRefs.current[String(moved.id)]
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }, 0)

    highlight(moved.id)
    setMoveForm({
      sourceChapterId: '',
      action: 'after',
      targetChapterId: '',
      targetPosition: '',
    })

    setIsMoveModalOpen(false)
  }

  const [agreementChapter, setAgreementChapter] = useState<AgreementChapter>({
    id: '',
    rta_id: null,
    agreement: '',
    xml_file: '',
  })

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      getAgreementById(axiosPrivate, {idAgreement})
        .then((res) => {
          if (res.status === 200) {
            const detailRes = res.data.data
            setAgreementChapter(detailRes)

            const contentsRes = detailRes.chapters
            setContents(contentsRes)
            setOriginalOrder(contentsRes.map((c) => c.id))
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to get data', 'error')
        })
        .finally(() => {
          setIsLoadingAgreement(false)
        })
    } catch (error) {
      console.error(error)
      setIsLoadingAgreement(false)
    }
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  const isOrderChanged = useMemo(() => {
    if (contents.length !== originalOrder.length) return true

    return contents.some((chapter, index) => chapter.id !== originalOrder[index])
  }, [contents, originalOrder])

  const handleSubmit = () => {
    // Only positions are sent: the server owns sort_order and the article_id
    // sequence derived from it, and a reorder must never rewrite content.
    const body = {
      rta_id: agreementChapter.rta_id ?? Number(idAgreement),
      chapters: contents.map((content, index) => ({
        id: content.id,
        sort_order: index + 1,
      })),
    }

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      submitReorderChapter(axiosPrivate, body)
        .then((res) => {
          if (res.status === 200) {
            showSwal('Order updated', undefined, 'success').then(() => {
              navigate(`/agreement/${idAgreement}`)
            })
          }
        })
        .catch((err) => {
          console.error(err)
          showSwal('Error', 'Failed to update data', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        navigate(`/agreement/${idAgreement}`)
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'agreement',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: agreementChapter.agreement ?? '-',
            path: `agreement/${idAgreement ?? agreementChapter.rta_id}`,
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Edit Order
      </PageTitle>
      <div className='AgreementChapterReorderPage'>
        {ability.can('update', subject) ? (
          <>
            <Row className='mb-5'>
              <Col>
                <Card>
                  <Card.Header className='card-header-stretch'>
                    <Card.Title className='w-100'>
                      <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                        <div className='title d-flex align-items-center'>
                          <span className='fa fa-file-alt px-2'></span>
                          <h3 className='text-gray-700'>Edit Order</h3>
                        </div>
                      </div>
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    {!isLoadingAgreement ? (
                      <Form className='form-agreement'>
                        <div className='row'>
                          <div className='col-12'>
                            <h3>RTA's Information</h3>
                          </div>
                          <div className='col-md-12 mb-3'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Name
                            </Form.Label>
                            <div className='data-value'>{agreementChapter.agreement}</div>
                            {agreementChapter.xml_file ? (
                              <a
                                href={agreementChapter.xml_file}
                                target='_blank'
                                rel='noreferrer'
                                className='text-link'
                              >
                                check XML File
                              </a>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12 py-5 d-flex align-items-center justify-content-between'>
                            <h3>List Content</h3>
                            <div className='header-action'>
                              <Button variant='outline-primary' onClick={() => openMoveModal()}>
                                Move Article
                              </Button>
                            </div>
                          </div>
                          <div className='col-12'>
                            <div className='table-agreement-chapter'>
                              <table className='table table-bordered align-middle'>
                                <thead>
                                  <tr>
                                    <th style={{width: '60px'}}>#</th>
                                    <th>Chapter Name</th>
                                    <th>Chapter Number</th>
                                    <th>Article Name</th>
                                    <th>Article Number</th>
                                    <th style={{width: '100px'}}>Chapter Id</th>
                                    <th style={{width: '100px'}}>Article Id</th>
                                    <th style={{width: '170px'}}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contents.length === 0 ? (
                                    <tr>
                                      <td colSpan={8} className='text-center text-muted'>
                                        No chapters available
                                      </td>
                                    </tr>
                                  ) : (
                                    contents.map((content, index) => (
                                      <tr
                                        key={content.id}
                                        ref={(el) => (rowRefs.current[String(content.id)] = el)}
                                        className={
                                          content.id === highlightedContentId
                                            ? 'chapter-row-highlight'
                                            : ''
                                        }
                                      >
                                        <td>{index + 1}</td>
                                        <td>
                                          {content.chapter_name === '' ? '-' : content.chapter_name}
                                        </td>
                                        <td>
                                          {content.chapter_number === ''
                                            ? '-'
                                            : content.chapter_number}
                                        </td>
                                        <td>
                                          {content.article_name === '' ? '-' : content.article_name}
                                        </td>
                                        <td>
                                          {content.article_number === ''
                                            ? '-'
                                            : content.article_number}
                                        </td>
                                        <td>{content.chapter_id}</td>
                                        <td>{content.article_id}</td>
                                        <td>
                                          <div className='d-flex gap-2'>
                                            {index > 0 && (
                                              <Button
                                                size='sm'
                                                variant='outline-secondary'
                                                onClick={() => handleMoveUp(index)}
                                              >
                                                ↑
                                              </Button>
                                            )}

                                            {index < contents.length - 1 && (
                                              <Button
                                                size='sm'
                                                variant='outline-secondary'
                                                onClick={() => handleMoveDown(index)}
                                              >
                                                ↓
                                              </Button>
                                            )}

                                            <Button
                                              size='sm'
                                              variant='outline-primary'
                                              onClick={() => openMoveModal(String(content.id))}
                                            >
                                              Move…
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div className='row pt-5'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isOrderChanged}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <div
                        className='d-flex align-items-center justify-content-center'
                        style={{height: '50vh'}}
                      >
                        <Spinner
                          animation='border'
                          role='status'
                          variant='primary'
                          className='me-3'
                        />
                        <div className='loading-text'>
                          Loading
                          <span className='dot'>.</span>
                          <span className='dot'>.</span>
                          <span className='dot'>.</span>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <ModalMoveChapter
              show={isMoveModalOpen}
              onHide={() => setIsMoveModalOpen(false)}
              onSubmit={handleMoveChapter}
              onValueChange={handleMoveValueChange}
              values={moveForm}
              chapters={contents}
              isLoading={isLoadingButton}
            />
          </>
        ) : (
          <NoPermissionCard />
        )}
      </div>
    </>
  )
}

export default AgreementChapterReorderPage
